import axiosInstance from './axiosInstance';

const BASE = '/api/product-comparison';
export type AiProvider = 'claude' | 'openai';
export type ComparisonVersion = 'v1' | 'v2';

export type V2RowStatus = 'match' | 'minor_difference' | 'mismatch' | 'missing';

export interface V2ComparisonRow {
  key: string;
  label: string;
  dabas: string | null;
  supplier: string | null;
  labelDoc: string | null;
  status: V2RowStatus;
  note: string | null;
}

export interface V2ComparisonSections {
  ingredients: V2ComparisonRow[];
  nutritionPer100g: V2ComparisonRow[];
  allergens: V2ComparisonRow[];
  productInfo: V2ComparisonRow[];
}

export interface V2ComparisonSummary {
  overallStatus: 'ok' | 'has_minor_differences' | 'has_mismatches' | 'has_regulatory_warnings';
  matches: number;
  minorDifferences: number;
  mismatches: number;
  missing: number;
}

export interface V2SourceAuditFile {
  fileName: string;
  mimeType: string;
  transport: 'document' | 'image' | 'text';
}

export interface V2SourceAudit {
  dabasMode: 'text';
  labelMode: 'text' | 'pdf_document' | 'image' | 'mixed' | 'word_text_fallback' | 'missing';
  supplierMode: 'text' | 'pdf_document' | 'image' | 'mixed' | 'word_text_fallback' | 'missing';
  labelFiles: V2SourceAuditFile[];
  supplierFile: V2SourceAuditFile | null;
}

export interface V2ComparisonResultData {
  comparisonVersion: 'v2';
  promptVersion: string;
  summary: V2ComparisonSummary;
  sections: V2ComparisonSections;
  regulatoryWarnings: string[];
  sourceAudit: V2SourceAudit;
  rawModelJson: unknown;
}

export interface ComparisonField {
  fieldKey: string;
  label: string;
  priority: number;
  damValue: string | null;
  pdfValue: string | null;
  pdfConfidence: 'high' | 'medium' | 'low' | null;
  wordValue: string | null;
  wordConfidence: 'high' | 'medium' | 'low' | null;
  status: 'match' | 'mismatch' | 'missing' | 'only_one_source';
  regulatoryFlags: string[];
  comparisonNote?: string | null;
}

export interface PdfRectCropRegion {
  pageNumber: number;
  xPct: number;
  yPct: number;
  widthPct: number;
  heightPct: number;
}

export interface LabelPdfCropSelection {
  pageRegions: PdfRectCropRegion[];
}

export interface LabelAiInputAudit {
  originalFileName: string;
  originalFilePath: string | null;
  aiFileName: string;
  aiFilePath: string | null;
  cropApplied: boolean;
  cropSelection: LabelPdfCropSelection | null;
}

export interface ComparisonResult {
  _id?: string;
  artikelnummer: string;
  produktnamn: string;
  assetId: string | null;
  aiProvider: AiProvider;
  aiModel: string;
  comparisonVersion?: ComparisonVersion;
  isTestRun?: boolean;
  testRunGroupId?: string | null;
  testRunIndex?: number | null;
  testRunTotal?: number | null;
  fields: ComparisonField[];
  v2Result?: V2ComparisonResultData | null;
  overallStatus: 'ok' | 'has_minor_differences' | 'has_mismatches' | 'has_regulatory_warnings';
  regulatoryWarnings: string[];
  pdfFileName: string | null;
  wordFileName: string | null;
  labelPdfCropSelections?: Array<LabelPdfCropSelection | null>;
  labelAiInputs?: LabelAiInputAudit[];
  extractedAt: string;
  rawLabelText?: string | null;
  rawSupplierText?: string | null;
}

export interface BatchResult {
  results: ComparisonResult[];
  summary: {
    total: number;
    ok: number;
    mismatches: number;
    regulatoryWarnings: number;
    notFound: number;
  };
}

export interface ComparisonHistoryItem {
  _id: string;
  artikelnummer: string;
  produktnamn: string;
  aiProvider?: AiProvider;
  aiModel?: string;
  comparisonVersion?: ComparisonVersion;
  isTestRun?: boolean;
  testRunGroupId?: string | null;
  testRunIndex?: number | null;
  testRunTotal?: number | null;
  overallStatus: string;
  regulatoryWarnings: string[];
  pdfFileName: string | null;
  wordFileName: string | null;
  labelAiInputs?: LabelAiInputAudit[];
  createdAt: string;
}

export interface ComparisonDetail {
  _id: string;
  artikelnummer: string;
  produktnamn: string;
  assetId: string | null;
  aiProvider?: AiProvider;
  aiModel?: string;
  comparisonVersion?: ComparisonVersion;
  isTestRun?: boolean;
  testRunGroupId?: string | null;
  testRunIndex?: number | null;
  testRunTotal?: number | null;
  fields: ComparisonField[];
  v2Result?: V2ComparisonResultData | null;
  overallStatus: string;
  regulatoryWarnings: string[];
  pdfFileName: string | null;
  pdfFilePath: string | null;
  wordFileName: string | null;
  wordFilePath: string | null;
  labelPdfCropSelections?: Array<LabelPdfCropSelection | null>;
  labelAiInputs?: LabelAiInputAudit[];
  resolvedFields: string[];
  rawLabelText?: string | null;
  rawSupplierText?: string | null;
  createdAt: string;
}

export interface AssetSearchResult {
  _id: string;
  title: string;
  artikelnummer: string;
}

export interface ExtractAndCompareOptions {
  labelCropBottomPctList?: number[];
  labelPdfCropSelections?: Array<LabelPdfCropSelection | null>;
  isTestRun?: boolean;
  testRunGroupId?: string | null;
  testRunIndex?: number | null;
  testRunTotal?: number | null;
}

export type HistoryMode = 'standard' | 'test';

export async function searchAssets(query: string): Promise<AssetSearchResult[]> {
  const { data } = await axiosInstance.get(`${BASE}/search/asset`, { params: { q: query } });
  return data;
}

export async function extractAndCompare(
  artikelnummer: string,
  pdfFiles: File[],
  supplierFile: File | null,
  provider: AiProvider,
  options?: ExtractAndCompareOptions,
): Promise<ComparisonResult> {
  const labelCropBottomPctList = options?.labelCropBottomPctList;
  const labelPdfCropSelections = options?.labelPdfCropSelections;
  const formData = new FormData();
  formData.append('artikelnummer', artikelnummer);
  formData.append('provider', provider);
  pdfFiles.forEach(f => formData.append('pdf', f));
  if (supplierFile) formData.append('supplier', supplierFile);
  if (options?.isTestRun) formData.append('isTestRun', 'true');
  if (options?.testRunGroupId) formData.append('testRunGroupId', options.testRunGroupId);
  if (typeof options?.testRunIndex === 'number') formData.append('testRunIndex', String(options.testRunIndex));
  if (typeof options?.testRunTotal === 'number') formData.append('testRunTotal', String(options.testRunTotal));
  if (Array.isArray(labelCropBottomPctList) && labelCropBottomPctList.length > 0) {
    formData.append('labelCropBottomPctList', JSON.stringify(labelCropBottomPctList));
  }
  if (Array.isArray(labelPdfCropSelections) && labelPdfCropSelections.length > 0) {
    formData.append('labelPdfCropSelections', JSON.stringify(labelPdfCropSelections));
  }

  const { data } = await axiosInstance.post(`${BASE}/extract`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000,
  });
  return data;
}

export async function extractAndCompareV2(
  artikelnummer: string,
  pdfFiles: File[],
  supplierFile: File | null,
  provider: AiProvider,
  options?: ExtractAndCompareOptions,
): Promise<ComparisonResult> {
  const labelPdfCropSelections = options?.labelPdfCropSelections;
  const formData = new FormData();
  formData.append('artikelnummer', artikelnummer);
  formData.append('provider', provider);
  pdfFiles.forEach(f => formData.append('pdf', f));
  if (supplierFile) formData.append('supplier', supplierFile);
  if (options?.isTestRun) formData.append('isTestRun', 'true');
  if (options?.testRunGroupId) formData.append('testRunGroupId', options.testRunGroupId);
  if (typeof options?.testRunIndex === 'number') formData.append('testRunIndex', String(options.testRunIndex));
  if (typeof options?.testRunTotal === 'number') formData.append('testRunTotal', String(options.testRunTotal));
  if (Array.isArray(labelPdfCropSelections) && labelPdfCropSelections.length > 0) {
    formData.append('labelPdfCropSelections', JSON.stringify(labelPdfCropSelections));
  }

  const { data } = await axiosInstance.post(`${BASE}/extract-v2`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
  });
  return data;
}

export async function batchCompare(
  artikelnummerList: string[],
  files: File[],
): Promise<BatchResult> {
  const formData = new FormData();
  artikelnummerList.forEach(art => formData.append('artikelnummer', art));
  files.forEach(f => formData.append('files', f));

  const { data } = await axiosInstance.post(`${BASE}/batch`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
  });
  return data;
}

export async function getHistory(page = 1, limit = 20, mode: HistoryMode = 'standard', q = ''): Promise<{
  comparisons: ComparisonHistoryItem[];
  total: number;
  page: number;
  limit: number;
  mode: HistoryMode;
}> {
  const { data } = await axiosInstance.get(`${BASE}/history`, { params: { page, limit, mode, q } });
  return data;
}

export async function getComparison(id: string): Promise<ComparisonDetail> {
  const { data } = await axiosInstance.get(`${BASE}/${id}`);
  return data;
}

export async function resolveFields(id: string, fieldKeys: string[]): Promise<{ resolvedFields: string[] }> {
  const { data } = await axiosInstance.put(`${BASE}/${id}/resolve`, { fieldKeys });
  return data;
}

export function getExportUrl(id: string): string {
  return `${BASE}/${id}/export`;
}

export function getDocumentUrl(id: string, type: 'pdf' | 'word', pdfIndex?: number | null): string {
  if (type === 'pdf' && typeof pdfIndex === 'number' && pdfIndex >= 0) {
    return `${BASE}/${id}/document/${type}?index=${pdfIndex}`;
  }
  return `${BASE}/${id}/document/${type}`;
}

export async function deleteComparison(id: string): Promise<void> {
  await axiosInstance.delete(`${BASE}/${id}`);
}

export async function deleteComparisons(ids: string[]): Promise<{ deleted: number }> {
  const { data } = await axiosInstance.delete(`${BASE}/history/bulk`, { data: { ids } });
  return data;
}
